import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { appointmentActions, fundingSourceActions, httpRequestsOnSuccessActions, systemActions } from 'store';
import {
    FindSuccessItem,
    FLAGGED_VALUES,
    PermissionList,
    RolePermission,
    SaveParams,
    scheduleViewEnum,
    splitCptServiceFilter,
    useModal,
    User,
} from 'utils';
import { SimpleModal } from 'components';
import { Filters, Selectable, CreateEvent } from './core';
import { ScheduleList } from './core/scheduleList';
import { CalendarFilters } from './core/calendarView/calendarFilters';
import moment from 'moment';

const RENDER_APPOINTMENTS_LIST = 'RENDER_APPOINTMENTS_LIST';
const RENDER_APPOINTMENTS_CATCH_LIST = 'RENDER_APPOINTMENTS_CATCH_LIST';
const CREATE_APPT = 'CREATE_APPOINTMENT';

export const ScheduleFragment = ({}) => {
    const { close } = useModal();
    const dispatch = useDispatch();
    const [openEvtModal, setOpen] = useState(false);
    const renderAppointments = FindSuccessItem(RENDER_APPOINTMENTS_LIST);
    const renderAppointmentsCatch = FindSuccessItem(RENDER_APPOINTMENTS_CATCH_LIST);
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const createApptSuccess = FindSuccessItem(CREATE_APPT);
    const isCalendar = info?.viewType === 'calendar';
    const { userInfo } = User();

    useEffect(() => {
        if (RolePermission([PermissionList.ELIGIBILITY_CHECK?.code])) {
            dispatch(systemActions.canUseIntegration());
        }
        // if (RolePermission([PermissionList.SYSTEM_SERVICE_TYPES?.code])) {
            // dispatch(systemActions.getServices());
            dispatch(fundingSourceActions.fundingUniqueServices());
        // }
    }, [dispatch]);

    useEffect(() => {
        const storedStateRaw = sessionStorage.getItem('calendarState');
        const apptDatesRaw = sessionStorage.getItem('apptDates');
        const hasStored = !!(storedStateRaw || apptDatesRaw);

        if (!userInfo && !hasStored) return;

        const bootstrapped = !info && hasStored;
        const pushInfo =
            info ? { ...info } :
                {
                  ...(storedStateRaw ? JSON.parse(storedStateRaw) : {}),
                  ...(apptDatesRaw ? JSON.parse(apptDatesRaw) : {}),
              };
        let changed = bootstrapped;

        if (userInfo) {
            if (!pushInfo?.viewType && userInfo?.scheduleView === scheduleViewEnum.CALENDAR) {
                pushInfo.viewType = 'calendar';
                // Default to the logged-in staff ONLY when the calendar isn't already
                // scoped to a specific staff or client (the button provides one of these).
                const hasStaff = pushInfo?.staffs?.length > 0;
                const hasClient = pushInfo?.clients?.length > 0;
                if (!hasStaff && !hasClient) {
                    pushInfo.ownStaff = true;
                    pushInfo.staffs = [userInfo?.id];
                    pushInfo.staffFull = [{ id: userInfo.id, firstName: userInfo.firstName, lastName: userInfo.lastName }];
                }
                changed = true;
            }

            if (pushInfo?.slot == null && userInfo?.calendarInterval) {
                pushInfo.slot = userInfo.calendarInterval;
                try {
                    const stored = JSON.parse(localStorage.getItem('userSettings')) || {};
                    if (stored.slot == null) {
                        localStorage.setItem('userSettings', JSON.stringify({ ...stored, slot: userInfo.calendarInterval }));
                    }
                } catch (e) {
                    /* corrupt userSettings — ignore */
                }
                changed = true;
            }
        }

        if (hasStored) {
            sessionStorage.removeItem('calendarState');
            if (apptDatesRaw) {
                const parsedDates = JSON.parse(apptDatesRaw);
                if (parsedDates?.firstDate === null && parsedDates?.lastDate === null) {
                    sessionStorage.removeItem('apptDates');
                }
            }
        }

        if (changed) SaveParams(navigate, pushInfo);
    }, [userInfo?.scheduleView, userInfo?.calendarInterval, userInfo?.id]);

    useEffect(() => {
        if (renderAppointments?.type === RENDER_APPOINTMENTS_LIST) {
            close();
            setOpen(false);
            getEvents('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(RENDER_APPOINTMENTS_LIST));
        }
        if (renderAppointments?.type === renderAppointmentsCatch) {
            getEvents('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(renderAppointmentsCatch));
        }
        if (createApptSuccess?.type === CREATE_APPT) {
            setOpen(false);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_APPT));
        }
    }, [renderAppointments, createApptSuccess, renderAppointmentsCatch]);

    useEffect(() => {
        const checkRequest = info?.viewType === 'calendar' ? info?.staffs?.length || info?.clients?.length : true;
        if (checkRequest) {
            getEvents('load');
            const timer = setInterval(() => {
                getEvents('noLoad');
            }, 30000);
            return () => clearInterval(timer);
        }
    }, [info]);

    const renderParams = useMemo(() => {
        const moment = require('moment');
        const currentDate = moment().utc();
        const currentWeekStart = currentDate.clone().startOf('week');
        const currentWeekEnd = currentDate.clone().endOf('week');
        const startDate = moment(info?.firstDate ? info?.firstDate : currentWeekStart);

        const filterInfo = {
            startDate: startDate.format('YYYY-MM-DD'),
        };
        if (info?.viewType === 'calendar') {
            delete filterInfo.skip;
            delete filterInfo.limit;
        } else {
            filterInfo.skip = info?.skip ? info?.skip : 0;
            filterInfo.limit = 50;
        }
        info?.searchKey ? (filterInfo.searchKey = info.searchKey) : delete filterInfo.searchKey;
        info?.searchValue ? (filterInfo.searchValue = info.searchValue) : delete filterInfo.searchValue;

        const endDate = moment(info?.lastDate ? info?.lastDate : currentWeekEnd);
        filterInfo.endDate = endDate.format('YYYY-MM-DD');

        if (info?.flag) {
            if (info?.flag === FLAGGED_VALUES.REMOVED_FROM_CLAIM) {
                filterInfo.removedFromClaimFlag = true;
            } else {
                filterInfo.incompleteUnitFlag = true;
            }
        } else {
            delete filterInfo.flag;
            delete filterInfo.removedFromClaimFlag;
            delete filterInfo.incompleteUnitFlag;
        }

        if (info?.sortType) {
            filterInfo.sortType = info.sortType;
            filterInfo.orderType = info.orderType;
        }

        if (info?.cptCode?.length) {
            const { cptCode, serviceTypeId } = splitCptServiceFilter(info.cptCode);
            cptCode?.length ? (filterInfo.cptCode = cptCode) : delete filterInfo.cptCode;
            serviceTypeId?.length ? (filterInfo.serviceTypeId = serviceTypeId) : delete filterInfo.serviceTypeId;
        } else {
            delete filterInfo.cptCode;
            delete filterInfo.serviceTypeId;
        }
        info?.staffs?.length ? (filterInfo.staffs = info.staffs) : delete filterInfo.staffs;
        info?.clients?.length ? (filterInfo.clients = info.clients) : delete filterInfo.clients;
        info?.types?.length ? (filterInfo.types = info.types?.map((i) => i?.id || i)) : delete filterInfo.types;
        info?.statuses?.length ? (filterInfo.statuses = info.statuses?.map((i) => i?.id || i)) : delete filterInfo.statuses;
        info?.errors?.length ? (filterInfo.errors = info.errors?.map((i) => i?.id || i)) : delete filterInfo.errors;
        info?.client ? (filterInfo.client = info.client) : delete filterInfo.client;
        info?.staff ? (filterInfo.staff = info.staff) : delete filterInfo.staff;
        info?.withoutSignature ? (filterInfo.withoutSignature = info.withoutSignature) : delete filterInfo.withoutSignature;
        info?.withoutClientSignature ? (filterInfo.withoutClientSignature = info.withoutClientSignature) : delete filterInfo.withoutClientSignature;
        info?.withoutProviderSignature ? (filterInfo.withoutProviderSignature = info.withoutProviderSignature) : delete filterInfo.withoutProviderSignature;
        info?.withoutNotes ? (filterInfo.withoutNotes = info.withoutNotes) : delete filterInfo.withoutNotes;
        info?.supervision ? (filterInfo.supervision = info.supervision) : delete filterInfo.supervision;
        info?.payer ? (filterInfo.fundingSource = info.payer) : delete filterInfo.fundingSource;

        return filterInfo;
    }, [info]);

    const getEvents = (load, month) => {
        dispatch(appointmentActions.getAppointment(renderParams, load));

        const currentMonth = month ? month : renderParams?.startDate;
        if (currentMonth && isCalendar) {
            const params = { ...renderParams };
            const m = moment(currentMonth);
            params.startDate = m.clone().startOf('month').format('YYYY-MM-DD');
            params.endDate = m.clone().endOf('month').format('YYYY-MM-DD');
            dispatch(appointmentActions.getCalendarAppointment(params));
        }
    };

    const handleOpenClose = () => {
        setOpen((prevState) => !prevState);
    };

    // const openCloseFullScreen = () => {
    //     const newFilters = {
    //         ...info,
    //     };
    //     !fullScreen ? newFilters.screenType = 'fullScreen' : delete newFilters.screenType;
    //     SaveParams(navigate, { ...newFilters });
    //     setFullScreen(!fullScreen);
    // };

    return (
        <div className={'flex-align-start full-width'}
             // style={{ background: isCalendar ? 'white' : 'transparent' }}
        >
            {isCalendar && info?.showCalendarFilters !== false && (
                <div>
                    <CalendarFilters getEvents={getEvents} />
                </div>
            )}
            <div className="full-width">
                <div className={isCalendar ? 'calendar-filter-box' : ''}>
                    <Filters handleOpen={handleOpenClose} />
                </div>
                <>{isCalendar ? <Selectable /> : <ScheduleList renderParams={renderParams} />}</>
            </div>

            <SimpleModal
                handleOpenClose={handleOpenClose}
                openDefault={openEvtModal}
                content={<CreateEvent handleOpenClose={handleOpenClose} />}
            />

            {/*<SimpleModal*/}
            {/*    handleOpenClose={openCloseFullScreen}*/}
            {/*    openDefault={fullScreen}*/}
            {/*    secondary={true}*/}
            {/*    bigSize={true}*/}
            {/*    content={*/}
            {/*        <CalendarPage*/}
            {/*            handleOpen={handleOpenClose}*/}
            {/*            handleOpenClose={handleOpenClose}*/}
            {/*            closeFullScreen={openCloseFullScreen}*/}
            {/*        />*/}
            {/*    }*/}
            {/*/>*/}
        </div>
    );
};
